import {
    Component, inject, Input
} from "@angular/core";
import { FormListItemDirective } from "../../directives/form-list-item/form-list-item.directive";
import { FormListComponent } from "../form-list/form-list.component";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { ButtonDirective } from "../../../application/directives/button/button.directive";

/**
 * Control buttons component for form list items that provides add, edit, and remove functionality.
 * Must be used within a FormListItemDirective context to access item data and type.
 *
 * Displays different controls based on the item type:
 * - "add" type: Shows an add button to create new items
 * - "item" type: Shows edit and remove buttons for existing items
 * - "requiredText" type: Shows an add button to create new items
 *
 * @example
 * ```html
 * <mgd-form-list [formControl]="listControl" [subform]="mySubform">
 *   <ng-container *mgdFormListItem="let item; type: 'item'">
 *     <span>{{ item.name }}</span>
 *     <mgd-form-list-item-controls></mgd-form-list-item-controls>
 *   </ng-container>
 *   <ng-container *mgdFormListItem="type: 'add'">
 *     <mgd-form-list-item-controls></mgd-form-list-item-controls>
 *   </ng-container>
 *  <ng-container *mgdFormListItem="type: 'requiredText'">
 *     <mgd-form-list-item-controls></mgd-form-list-item-controls>
 *   </ng-container>
 * </mgd-form-list>
 * ```
 */
@Component({
    selector: "mgd-form-list-item-controls",
    imports: [
        MatIcon,
        MatIconButton,
        ButtonDirective
    ],
    templateUrl: "./form-list-item-controls.component.html",
    styleUrl: "./form-list-item-controls.component.scss"
})
export class FormListItemControlsComponent {

    /**
     * The data associated with the current form list item.
     * Only relevant for "item" type items. Should be undefined for "add" type items.
     */
    @Input() data?: unknown;

    /**
     * The index of the current form list item.
     * Only relevant for "item" type items. Should be undefined for "add" type items.
     */
    @Input() index?: number;

    /**
     * Determines if the current context is an "add" type item.
     * Used to conditionally display the add button.
     *
     * @returns True if the item type is "add"
     */
    get isAdd(): boolean {
        return this._formListItem.type === "add";
    }

    /**
     * Determines if the current context is an "item" type (existing item).
     * Used to conditionally display edit and remove buttons.
     *
     * @returns True if the item type is "item"
     */
    get isItem(): boolean {
        return this._formListItem.type === "item";
    }

    /**
     * Determines if the current context is a "requiredText" type.
     * Used to conditionally display the required text.
     *
     * @returns True if the item type is "requiredText"
     */
    get isRequiredText(): boolean {
        return this._formListItem.type === "requiredText";
    }

    /**
     * Determines if the control should be disabled,
     * should be disabled when subform is currently open.
     *
     * @returns True if the subform is open
     */
    get disabled(): boolean {
        return this._formList?.disabled;
    }

    /** Injected FormListItemDirective from the host element to access item context */
    private readonly _formListItem? = inject(FormListItemDirective, { host: true });

    /** Injected FormListComponent from the host element to perform list operations */
    private readonly _formList? = inject(FormListComponent, { host: true });

    /**
     * Handles edit button click events.
     * Opens the associated subform with the current item's data for editing.
     * Only available for "item" type contexts.
     */
    onEditItemClick(): void {
        this._formListItem.active = true;
        // call openSubform with data and index
        this._formList.subform?.openSubform(this.data, this.index);
    }

    /**
     * Handles remove button click events.
     * Removes the current item from the form list.
     * Only available for "item" type contexts.
     */
    onRemoveItemClick(): void {
        this._formList.removeItem(this.data);
    }

    /**
     * Handles add button click events.
     * Opens the associated subform without data to create a new item.
     * Only available for "add" type contexts.
     */
    onAddItemClick(): void {
        this._formListItem.active = true;
        this._formList.subform?.openSubform();
    }
}
